import React, { useState, useEffect } from "react";
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native-web";
import { COLORS } from "constants/Colors";
import { PrimaryText } from "components/Common/PrimaryText";
import * as FaIcons from "react-icons/fa";
import { useCampaigns } from "context/CampaignsContext";
import QRCode from "qrcode";

// Cloud Functions base URL - update this to your Firebase project
const CLOUD_FUNCTIONS_URL =
  "https://us-central1-foreverx-98fbd.cloudfunctions.net/app";

export const GenerateQRModal = ({ showModal, setShowModal }) => {
  const { selectedCampaign } = useCampaigns();
  const [qrImageUrl, setQrImageUrl] = useState("");

  // Generate QR code on mount when campaign is selected
  useEffect(() => {
    const generateQR = async () => {
      if (!selectedCampaign) return;

      // Cloud Functions URL
      const landingUrl = `${CLOUD_FUNCTIONS_URL}/campaign/landing?id=${selectedCampaign.id}`;

      try {
        const imageUrl = await QRCode.toDataURL(landingUrl, {
          width: 400,
          margin: 2,
          color: {
            dark: "#000000",
            light: "#FFFFFF",
          },
        });
        setQrImageUrl(imageUrl);
      } catch (error) {
        console.error("Error generating QR code:", error);
        alert(`Error generating QR code: ${error.message}`);
      }
    };

    generateQR();
  }, [selectedCampaign]);

  const handleDownloadQR = () => {
    if (!qrImageUrl || !selectedCampaign) return;

    const link = document.createElement("a");
    link.href = qrImageUrl;
    link.download = `QR-${selectedCampaign.id}.png`;
    link.click();
  };

  const handleCopyUrl = () => {
    if (!selectedCampaign) return;

    const landingUrl = `${CLOUD_FUNCTIONS_URL}/campaign/landing?id=${selectedCampaign.id}`;
    navigator.clipboard.writeText(landingUrl);
    alert("Landing URL copied to clipboard!");
  };

  if (!showModal) return null;

  return (
    <View style={styles.modalOverlay}>
      <View style={styles.modalContainer}>
        {/* Header */}
        <View style={styles.header}>
          <PrimaryText
            fontSize={24}
            fontWeight={800}
            style={{ color: COLORS.primary.darkGray }}
          >
            Campaign QR Code
          </PrimaryText>
          <TouchableOpacity
            onPress={() => setShowModal(false)}
            style={styles.closeButton}
          >
            <FaIcons.FaTimes size={20} color={COLORS.primary.darkGray} />
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.content}>
          {/* QR Code Card */}
          <View style={styles.card}>
            <PrimaryText
              fontSize={18}
              fontWeight={700}
              style={{ marginBottom: 16, color: COLORS.primary.darkGray }}
            >
              Store QR Code
            </PrimaryText>
            <PrimaryText
              fontSize={13}
              style={{ color: COLORS.primary.gray, marginBottom: 20 }}
            >
              Print this QR code and display it at your store location.
              Customers will scan it to get their unique promo code.
            </PrimaryText>

            {qrImageUrl ? (
              <View style={styles.qrContainer}>
                <img
                  src={qrImageUrl}
                  alt="Store QR Code"
                  style={{ width: 300, height: 300 }}
                />
              </View>
            ) : (
              <View style={styles.qrPlaceholder}>
                <ActivityIndicator size="large" color={COLORS.primary.blue} />
                <PrimaryText
                  fontSize={14}
                  style={{ color: COLORS.primary.gray, marginTop: 12 }}
                >
                  Generating QR code...
                </PrimaryText>
              </View>
            )}

            {selectedCampaign && (
              <View style={styles.campaignInfo}>
                <PrimaryText
                  fontSize={16}
                  fontWeight={700}
                  style={{ marginBottom: 8, color: COLORS.primary.darkGray }}
                >
                  {selectedCampaign.name}
                </PrimaryText>
                <PrimaryText
                  fontSize={14}
                  fontWeight={600}
                  style={{ marginBottom: 4, color: COLORS.primary.blue }}
                >
                  {selectedCampaign.promoLabel}
                </PrimaryText>
                <PrimaryText
                  fontSize={12}
                  style={{ color: COLORS.primary.gray }}
                >
                  {selectedCampaign.storeAddress}
                </PrimaryText>
              </View>
            )}

            <View style={styles.actionsContainer}>
              <TouchableOpacity
                onPress={handleDownloadQR}
                style={[styles.button, styles.primaryButton]}
                disabled={!qrImageUrl}
              >
                <FaIcons.FaDownload size={16} color={COLORS.primary.white} />
                <PrimaryText
                  fontSize={16}
                  fontWeight={600}
                  style={{ marginLeft: 8, color: COLORS.primary.white }}
                >
                  Download QR Code
                </PrimaryText>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={handleCopyUrl}
                style={[styles.button, styles.secondaryButton]}
                disabled={!selectedCampaign}
              >
                <FaIcons.FaRegCopy size={16} color={COLORS.primary.white} />
                <PrimaryText
                  fontSize={16}
                  fontWeight={600}
                  style={{ marginLeft: 8, color: COLORS.primary.white }}
                >
                  Copy URL
                </PrimaryText>
              </TouchableOpacity>
            </View>
          </View>

          {/* Landing URL Info */}
          {selectedCampaign && (
            <View style={styles.card}>
              <PrimaryText
                fontSize={16}
                fontWeight={700}
                style={{ marginBottom: 16, color: COLORS.primary.darkGray }}
              >
                Landing Page URL
              </PrimaryText>
              <View style={styles.urlContainer}>
                <PrimaryText
                  fontSize={13}
                  style={{ textAlign: "center", color: COLORS.primary.gray }}
                >
                  {`${CLOUD_FUNCTIONS_URL}/campaign/landing?id=${selectedCampaign.id}`}
                </PrimaryText>
              </View>
              <PrimaryText
                fontSize={12}
                style={{ marginTop: 8, color: COLORS.primary.gray }}
              >
                This is the URL where customers will land when they scan the QR
                code. They will receive a unique promo code (cookie-protected).
              </PrimaryText>
            </View>
          )}

          {/* Info Box */}
          <View style={styles.infoBox}>
            <PrimaryText
              fontSize={14}
              fontWeight={600}
              style={{ color: "#1565C0" }}
            >
              ℹ️ How It Works
            </PrimaryText>
            <PrimaryText
              fontSize={13}
              style={{ color: "#424242", marginTop: 8 }}
            >
              1. Print this QR code and display it prominently at your store
            </PrimaryText>
            <PrimaryText fontSize={13} style={{ color: "#424242" }}>
              2. Customers scan the QR code with their phone camera
            </PrimaryText>
            <PrimaryText fontSize={13} style={{ color: "#424242" }}>
              3. They land on the campaign page and get a unique promo code
            </PrimaryText>
            <PrimaryText fontSize={13} style={{ color: "#424242" }}>
              4. They download the ForeverX app and claim their promo code
            </PrimaryText>
            <PrimaryText
              fontSize={13}
              style={{ color: "#424242", marginTop: 8 }}
            >
              Each customer gets a unique code (anti-fraud protection via
              cookies)
            </PrimaryText>
          </View>
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modalContainer: {
    backgroundColor: COLORS.primary.white,
    borderRadius: 16,
    width: "90%",
    maxWidth: 600,
    maxHeight: "90%",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 5,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.primary.lightGray,
  },
  closeButton: {
    padding: 8,
  },
  content: {
    padding: 24,
  },
  card: {
    backgroundColor: "#F8F9FA",
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
  },
  qrContainer: {
    alignItems: "center",
    padding: 20,
    backgroundColor: COLORS.primary.white,
    borderRadius: 12,
    marginBottom: 20,
  },
  qrPlaceholder: {
    width: 300,
    height: 300,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: COLORS.primary.lightGray,
    borderRadius: 12,
    marginBottom: 20,
    alignSelf: "center",
  },
  campaignInfo: {
    backgroundColor: COLORS.primary.white,
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
    alignItems: "center",
  },
  actionsContainer: {
    flexDirection: "row",
    gap: 12,
  },
  button: {
    flex: 1,
    flexDirection: "row",
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  primaryButton: {
    backgroundColor: COLORS.primary.blue,
  },
  secondaryButton: {
    backgroundColor: "#757575",
  },
  urlContainer: {
    backgroundColor: COLORS.primary.white,
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  infoBox: {
    backgroundColor: "#E3F2FD",
    borderLeftWidth: 4,
    borderLeftColor: "#1976D2",
    borderRadius: 8,
    padding: 16,
  },
});
